import React, { useState } from 'react';
import { CalendarDays, Clock, Flag, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const PRIORITIES = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export const formatDue = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : null);
export const isOverdue = (task) => Boolean(task.due_date) && task.status !== 'done' && new Date(`${task.due_date}T23:59:59`) < new Date();

// Read-only chips: priority · due date · estimate.
export const TaskMeta = ({ task }) => {
  if (!task.priority && !task.due_date && task.estimated_hours == null) return null;
  return (
    <div className="task-meta-row" data-testid="task-meta">
      {task.priority && <span className={`task-tag priority-${task.priority}`} data-testid="task-meta-priority"><Flag size={11} /> {task.priority}</span>}
      {task.due_date && (
        <span className={`task-tag due ${isOverdue(task) ? 'overdue' : ''}`} data-testid="task-meta-due"><CalendarDays size={11} /> Due {formatDue(task.due_date)}</span>
      )}
      {task.estimated_hours != null && <span className="task-tag" data-testid="task-meta-hours"><Clock size={11} /> {task.estimated_hours}h</span>}
    </div>
  );
};

// Tick-off checklist; any team member can tick (progress, not a task change → no approval).
export const SubtaskChecklist = ({ task, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const subtasks = task.subtasks || [];
  if (!subtasks.length) return <p className="detail-text muted" data-testid="subtasks-empty">No subtasks yet.</p>;
  const done = subtasks.filter((s) => s.done).length;

  const toggle = async (index, checked) => {
    setBusy(index);
    try {
      const data = await commsApi.toggleSubtask(task.id, index, Boolean(checked));
      onChanged?.({ ...task, subtasks: data.subtasks, updated_at: data.updated_at });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update the subtask');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="subtask-list" data-testid="subtask-checklist">
      <div className="subtask-progress" data-testid="subtask-progress">
        <div className="subtask-bar"><span style={{ width: `${(done / subtasks.length) * 100}%` }} /></div>
        <span>{done}/{subtasks.length} done</span>
      </div>
      {subtasks.map((s, i) => (
        <label key={`${task.id}-${i}`} className={`subtask-item ${s.done ? 'done' : ''}`} data-testid={`subtask-${i}`}>
          <Checkbox checked={s.done} disabled={busy === i} onCheckedChange={(c) => toggle(i, c)} data-testid={`subtask-checkbox-${i}`} />
          <span>{s.title}</span>
        </label>
      ))}
    </div>
  );
};

// Editable priority / hours / due date / subtasks used by Add Task and the details editor.
export const TaskExtrasEditor = ({ value, onChange, testPrefix = 'task' }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const subtasks = value.subtasks || [];
  const setSubtask = (i, title) => set({ subtasks: subtasks.map((s, j) => (j === i ? { ...s, title } : s)) });
  return (
    <div className="extras-editor">
      <div className="extras-grid">
        <label className="draft-field">
          <span>Priority</span>
          <Select value={value.priority || 'none'} onValueChange={(v) => set({ priority: v === 'none' ? null : v })}>
            <SelectTrigger data-testid={`${testPrefix}-priority`}><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No priority</SelectItem>
              {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="draft-field">
          <span>Estimated hours</span>
          <Input type="number" min="0" step="0.5" value={value.estimated_hours ?? ''} onChange={(e) => set({ estimated_hours: e.target.value })} placeholder="—" data-testid={`${testPrefix}-hours`} />
        </label>
        <label className="draft-field">
          <span>Due date</span>
          <Input type="date" value={value.due_date || ''} onChange={(e) => set({ due_date: e.target.value })} data-testid={`${testPrefix}-due`} />
        </label>
      </div>
      <div className="draft-field">
        <span>Subtasks</span>
        <div className="draft-subtasks">
          {subtasks.map((s, i) => (
            <div className="draft-subtask" key={`sub-${i}`}>
              <Input value={s.title} onChange={(e) => setSubtask(i, e.target.value)} placeholder={`Step ${i + 1}`} data-testid={`${testPrefix}-subtask-${i}`} />
              <button type="button" className="draft-remove" onClick={() => set({ subtasks: subtasks.filter((_, j) => j !== i) })} aria-label="Remove subtask" data-testid={`${testPrefix}-subtask-remove-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button type="button" className="draft-add" onClick={() => set({ subtasks: [...subtasks, { title: '', done: false }] })} data-testid={`${testPrefix}-subtask-add`}>
            <Plus size={14} /> Add subtask
          </button>
        </div>
      </div>
    </div>
  );
};

// Normalise editor state → API payload fields.
export const extrasPayload = (value) => ({
  priority: value.priority || null,
  estimated_hours: value.estimated_hours === '' || value.estimated_hours == null ? null : Number(value.estimated_hours),
  due_date: value.due_date || null,
  subtasks: (value.subtasks || []).map((s) => ({ title: (s.title || '').trim(), done: Boolean(s.done) })).filter((s) => s.title),
});

// Text read aloud by the "Read task aloud" button.
export const taskSpeechText = (task, assigneeName) => [
  task.title,
  task.description,
  task.priority ? `Priority ${task.priority}.` : '',
  task.due_date ? `Due ${formatDue(task.due_date)}.` : '',
  task.estimated_hours != null ? `Estimated ${task.estimated_hours} hours.` : '',
  assigneeName ? `Assigned to ${assigneeName}.` : '',
  task.subtasks?.length ? `Subtasks: ${task.subtasks.map((s, i) => `${i + 1}. ${s.title}${s.done ? ', done' : ''}`).join('. ')}.` : '',
].filter(Boolean).join(' ');
